import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import type { TreeViewProps, TreeViewRow } from './treeTypes';
import {
  deriveCheckStates,
  deriveVisibleRows,
  getLevelBaselineOverrides,
  getPathOverrides,
  toggleNodeChecked,
} from './treeViewModel';
import { TreeViewRowComponent } from './TreeViewRow';
import { TreeViewFilters } from './TreeViewFilters';
import { TreeViewLevelDots } from './TreeViewLevelDots';

/**
 * Sdílená tree view komponenta nad normalizovaným datovým kontraktem,
 * vzhled a chování dle specifikace „Anatomie tree-view" (PHX-TREE-01 v1.1).
 *
 * Consumer předává `TreeData` (nodesById, rootIds, typeDefinitions, actionsByType)
 * a komponenta odvozuje render model: normalizované vyhledávání (bez diakritiky,
 * vč. ł → l) se zvýrazněním shody v textu, varianta B „rozbalit lze vždy + filtr
 * uzlu" se štítkem částečné shody, tečky hromadného rozbalení po úrovních
 * a návrat k výběru po zrušení hledání.
 *
 * Priority (§ 8): rozbalení ruční zásah > auto-expand při hledání > úroveň dle
 * tečky; výpis potomků odkrytí štítkem > filtr hledání > úplný výpis.
 *
 * ARIA keyboard baseline: šipky, Enter/Space, Home/End
 */
export function TreeView({
  data,
  activeNodeId: controlledActiveNodeId,
  onNodeActivate,
  defaultExpandedIds,
  className,
  afterFilters,
  levelTypeIds,
  defaultExpandLevel,
  checkable,
  checkedIds,
  onCheckedChange,
}: TreeViewProps) {
  const { t } = useTranslation('sharedUi');
  const treeRef = useRef<HTMLDivElement>(null);

  // Vnitřní stav
  const [query, setQuery] = useState('');
  const visibleTypes = useMemo(
    () => new Set(data.typeDefinitions.map((td) => td.id)),
    [data.typeDefinitions]
  );
  // Výchozí stav agendy je úroveň 2 (PHX-TREE-01 § 3); bez teček úrovní 1 (sbaleno).
  const [baseLevel, setBaseLevel] = useState(defaultExpandLevel ?? (levelTypeIds ? 2 : 1));
  const [manualOverrides, setManualOverrides] = useState<Map<string, boolean>>(() => {
    const base = new Map<string, boolean>();
    for (const id of defaultExpandedIds ?? []) base.set(id, true);
    return base;
  });
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const [internalActiveNodeId, setInternalActiveNodeId] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const [scrollToNodeId, setScrollToNodeId] = useState<string | null>(null);

  const activeNodeId =
    controlledActiveNodeId !== undefined ? controlledActiveNodeId : internalActiveNodeId;

  // Typový slovník pro rychlý lookup ikon
  const typeMap = useMemo(
    () => new Map(data.typeDefinitions.map((td) => [td.id, td])),
    [data.typeDefinitions]
  );

  // Derivace viditelných řádků
  const { rows, normalizedQuery } = useMemo(
    () =>
      deriveVisibleRows(data, {
        query,
        visibleTypes,
        manualOverrides,
        revealedIds,
        baseLevel,
        activeNodeId,
      }),
    [data, query, visibleTypes, manualOverrides, revealedIds, baseLevel, activeNodeId]
  );

  const handleToggleExpand = useCallback(
    (nodeId: string) => {
      const row = rows.find((r) => r.node.id === nodeId);
      if (!row || !row.hasChildren) return;
      setManualOverrides((prev) => {
        const next = new Map(prev);
        next.set(nodeId, !row.isExpanded);
        return next;
      });
    },
    [rows]
  );

  // Klik na štítek odkryje výpis uzlu (a uzel ponechá rozbalený)
  const handleToggleReveal = useCallback((nodeId: string) => {
    setRevealedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
    setManualOverrides((prev) => {
      const next = new Map(prev);
      next.set(nodeId, true);
      return next;
    });
  }, []);

  // Každá změna hledaného textu resetuje ruční zásahy i odkrytí (§ 4);
  // zrušení hledání s výběrem navíc rozbalí cestu k výběru a najede na něj (§ 6).
  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
      setRevealedIds(new Set());
      if (value.trim() === '' && activeNodeId) {
        setManualOverrides(getPathOverrides(data, activeNodeId));
        setScrollToNodeId(activeNodeId);
      } else {
        setManualOverrides(new Map());
      }
    },
    [data, activeNodeId]
  );

  // Klik na tečku = tvrdý reset rozbalení na úroveň (§ 3), funguje i při hledání
  const handleLevelClick = useCallback(
    (level: number) => {
      setBaseLevel(level);
      setRevealedIds(new Set());
      if (query.trim() !== '') {
        setManualOverrides(getLevelBaselineOverrides(data, level));
      } else {
        setManualOverrides(new Map());
      }
    },
    [data, query]
  );

  // Svislé najetí na vybraný řádek (přibližně doprostřed výřezu)
  useEffect(() => {
    if (!scrollToNodeId) return;
    const container = treeRef.current;
    if (container) {
      const el = container.querySelector<HTMLElement>(
        `[data-node-id="${CSS.escape(scrollToNodeId)}"]`
      );
      if (el) {
        // +10 = polovina výšky řádku (20 px), aby byl řádek opticky uprostřed
        container.scrollTop = Math.max(0, el.offsetTop - container.clientHeight / 2 + 10);
      }
    }
    setScrollToNodeId(null);
  }, [scrollToNodeId]);

  const handleActivate = useCallback(
    (nodeId: string) => {
      if (onNodeActivate) {
        onNodeActivate(nodeId);
      } else {
        setInternalActiveNodeId(nodeId);
      }
    },
    [onNodeActivate]
  );

  // Tri-state checkboxů (jen v checkable režimu)
  const checkStates = useMemo(
    () => (checkable ? deriveCheckStates(data, checkedIds ?? new Set()) : null),
    [checkable, data, checkedIds]
  );

  const handleCheckToggle = useCallback(
    (nodeId: string) => {
      onCheckedChange?.(toggleNodeChecked(data, checkedIds ?? new Set(), nodeId));
    },
    [data, checkedIds, onCheckedChange]
  );

  // ARIA keyboard navigace
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const handleNavigation = (newIndex: number) => {
        e.preventDefault();
        const clamped = Math.max(0, Math.min(rows.length - 1, newIndex));
        setFocusedIndex(clamped);
        // Focus příslušný DOM element
        const container = treeRef.current;
        if (container) {
          const row = rows[clamped];
          if (row) {
            const el = container.querySelector<HTMLElement>(
              `[data-node-id="${CSS.escape(row.node.id)}"]`
            );
            el?.focus();
          }
        }
      };

      switch (e.key) {
        case 'ArrowDown':
          handleNavigation(focusedIndex + 1);
          break;
        case 'ArrowUp':
          handleNavigation(focusedIndex - 1);
          break;
        case 'ArrowRight': {
          const currentRow = rows[focusedIndex];
          if (currentRow?.hasChildren && !currentRow.isExpanded) {
            e.preventDefault();
            handleToggleExpand(currentRow.node.id);
          } else if (currentRow?.isExpanded) {
            // Move focus na prvního potomka
            handleNavigation(focusedIndex + 1);
          }
          break;
        }
        case 'ArrowLeft': {
          const currentRow = rows[focusedIndex];
          if (currentRow?.isExpanded && currentRow.hasChildren) {
            e.preventDefault();
            handleToggleExpand(currentRow.node.id);
          } else if (currentRow?.node.parentId) {
            // Move focus na rodiče
            const parentIndex = rows.findIndex((r) => r.node.id === currentRow.node.parentId);
            if (parentIndex >= 0) handleNavigation(parentIndex);
          }
          break;
        }
        case 'Home':
          handleNavigation(0);
          break;
        case 'End':
          handleNavigation(rows.length - 1);
          break;
        case 'Enter':
        case ' ': {
          e.preventDefault();
          const currentRow = rows[focusedIndex];
          if (currentRow) {
            handleActivate(currentRow.node.id);
          }
          break;
        }
      }
    },
    [rows, focusedIndex, handleToggleExpand, handleActivate]
  );

  const actions = data.actionsByType ?? [];

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      <TreeViewFilters query={query} onQueryChange={handleQueryChange} />
      {levelTypeIds && (
        <TreeViewLevelDots
          levelCount={levelTypeIds.length}
          baseLevel={baseLevel}
          onLevelClick={handleLevelClick}
        />
      )}
      {afterFilters}

      {rows.length === 0 ? (
        <div className="py-phx-7 px-phx-4 text-center">
          <p className="text-phx-text-2 text-[13px]">
            {query.trim() ? t('treeView.noResults') : t('treeView.empty')}
          </p>
          {query.trim() !== '' && (
            <p className="text-phx-text-muted text-[11.5px]">{t('treeView.noResultsHint')}</p>
          )}
        </div>
      ) : (
        <div
          ref={treeRef}
          role="tree"
          aria-label="Tree"
          onKeyDown={handleKeyDown}
          className="relative min-h-0 flex-1 overflow-auto pt-0.5 pb-2.5"
        >
          {rows.map((row: TreeViewRow, index: number) => (
            <TreeViewRowComponent
              key={row.node.id}
              row={checkStates ? { ...row, checkState: checkStates.get(row.node.id) } : row}
              actions={actions}
              onCheckToggle={checkStates ? handleCheckToggle : undefined}
              typeIcon={typeMap.get(row.node.type)?.icon}
              normalizedQuery={normalizedQuery}
              onToggleExpand={handleToggleExpand}
              onToggleReveal={handleToggleReveal}
              onActivate={(nodeId) => {
                setFocusedIndex(index);
                handleActivate(nodeId);
              }}
              isFocused={index === focusedIndex}
            />
          ))}
        </div>
      )}
    </div>
  );
}
